"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, ChevronRight } from "lucide-react";
import FadeIn from "../animations/FadeIn";
import CardCarousel from "./CardCarousel";

export interface TreatmentItem {
    id: string | number;
    link: string;
    image: string;
    title: string;
}

type TreatmentsDict = {
    home?: { treatments_title?: string; see_details?: string; see_all_treatments?: string };
    header?: { services?: string };
};

export default function TreatmentsCarousel({
    treatments,
    title,
    description,
    dict,
    seeAllHref,
}: {
    treatments: TreatmentItem[];
    title?: string;
    description?: string;
    dict?: TreatmentsDict;
    seeAllHref?: string;
}) {
    const card = (item: TreatmentItem) => (
        <Link href={item.link} className="group relative flex h-[340px] sm:h-[380px] lg:h-[360px] rounded-[20px] overflow-hidden shadow-[0_12px_30px_rgba(5,30,60,0.12)] hover:shadow-[0_20px_45px_rgba(5,30,60,0.22)] ring-1 ring-black/5 transition-all duration-500 hover:-translate-y-1.5 bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#00AEEF]/50">
            <Image
                src={item.image}
                alt={item.title}
                fill
                quality={75}
                sizes="(max-width: 640px) 80vw, (max-width: 1024px) 46vw, 25vw"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#051e3c] via-[#0f4c81]/30 to-transparent opacity-90 transition-opacity group-hover:opacity-95"></div>

            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                <h3 className="text-white font-bold text-lg sm:text-xl leading-tight tracking-tight">{item.title}</h3>
                {/* Dokunmatik ekranlarda hover olmadigi icin mobilde hep gorunur. */}
                <span className="mt-2 inline-flex items-center text-white/85 text-sm font-medium transition-all duration-300 lg:opacity-0 lg:-translate-y-1 lg:group-hover:opacity-100 lg:group-hover:translate-y-0 lg:group-focus-visible:opacity-100">
                    {dict?.home?.see_details} <ChevronRight className="w-3.5 h-3.5 ml-1" aria-hidden="true" />
                </span>
            </div>
        </Link>
    );

    return (
        <section className="py-16 lg:py-20 bg-gradient-to-b from-gray-50 to-[#F0F7FF] relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/30 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

            <div className="container-custom relative z-10">
                <FadeIn>
                    <div className="mb-8 flex flex-col gap-5 md:mb-12 md:flex-row md:items-end md:justify-between">
                        <div className="max-w-2xl">
                            <h2 className="text-3xl font-bold tracking-[-0.03em] text-[#051e3c] md:text-[2.5rem] md:leading-[1.1]">
                                {title || dict?.home?.treatments_title || dict?.header?.services || "Treatments"}
                            </h2>
                            {description && (
                                <p className="mt-4 text-[16px] leading-relaxed text-gray-600">
                                    {description}
                                </p>
                            )}
                        </div>
                        {seeAllHref && dict?.home?.see_all_treatments ? (
                            <Link
                                href={seeAllHref}
                                className="group inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-[#0f4c81] px-5 py-2.5 text-[14px] font-semibold text-white shadow-sm transition-colors hover:bg-[#0a355c] md:self-auto"
                            >
                                {dict.home.see_all_treatments}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                            </Link>
                        ) : null}
                    </div>
                </FadeIn>

                {treatments.length === 0 ? (
                    <div className="py-10 text-center text-gray-600">
                        No active services found.
                    </div>
                ) : (
                    <>
                        {/* Desktop: grid */}
                        <div className="hidden lg:grid grid-cols-4 gap-6">
                            {treatments.map((item, index) => (
                                <FadeIn key={item.id} delay={index * 80} className="h-full">
                                    {card(item)}
                                </FadeIn>
                            ))}
                        </div>

                        {/* Mobile / tablet: carousel */}
                        <div className="lg:hidden">
                            <CardCarousel
                                ariaLabel="treatment"
                                itemClassName="w-[80%] sm:w-[46%] md:w-[38%]"
                                items={treatments.map((item) => card(item))}
                            />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
